// src/components/GuestFormViewV2.jsx — REFONTE DU FORMULAIRE DE BIENVENUE (V2)
//
// Rendu PRÉSENTATIONNEL neuf du formulaire voyageur, monté sur la grammaire de
// l'espace séjour (plan FORMULAIRE_REFONTE_PLAN_V2, maquette
// docs/handoff/formulaire-proto.html — le dessin fait foi, textes au mot près).
//
// MÊME CONTRAT DE PROPS que GuestFormView (formConfig, schedule, idDocRequired,
// billingAddressRequired, values, uploads, lang, step, error, callbacks,
// previewMode) + additions V2 :
//   guestFirstName    prénom de la résa (« Bonjour Marta ») — null → « Bonjour »
//   guestDisplayName  prénom + nom (ligne verte du document) — jamais le nom lu
//                     sur la pièce, toujours celui de la RÉSA (pièce 3 du plan)
//   journeyUrl        lien espace séjour (écran merci, CTA conditionnel)
//   fieldErrors       carte d'erreurs par reference (B2 : les erreurs sous champ
//                     vivent ICI, le conteneur ne fait que produire la carte)
//   onRemoveIdFile    ✕ = action id-upload-remove réelle (M3)
//
// Briques réutilisées de l'espace séjour : charte C/F (charte-sejour), Bandeau
// (prop additive `photo`), logo couleur LOGO_B64 (verdict arrêt 1 — jamais le
// pochoir blanc sans disque). Cartes-sections, ligne fichier verte, encart
// ambre, cibles photo/fichier : NEUFS ici (n'existaient nulle part).
//
// Ce fichier ne fait AUCUN fetch : il reçoit l'état + des callbacks.

import { useEffect, useRef } from "react"
import { C, F } from "../lib/charte-sejour"
import { Bandeau } from "../SejourV2"
// LOGO_B64 = le logo COMPLET (lotus + son disque bleu clair). Verdict Dimitri
// (revue arrêt 1, 2026-09-06) : le pochoir blanc SANS disque (Lotus) est la
// mauvaise variante — posé sur le navy il ne reste que des traits flottants.
// Même correction que l'espace voyageur (SejourV2Ecran/LigneMarque).
import { LOGO_B64 } from "../assets/logo"
import {
  t, FORM_LOCALES, pieceOptionLabel, FORM_CONTACT_EMAIL, texteLimiteModification,
} from "../lib/form-i18n"
import { shouldRenderField } from "./GuestFormView"
import { resolveCoverPhoto } from "../lib/form-cover-photo"

// ── Sentinelle « après 23h » (M4, Q-APRES-23H) ─────────────────────────────
// Valeur canonique transmise TELLE QUELLE au serveur (jamais un faux 23h30).
// Miroir CJS : lib/form-submit-handlers.js LATE_ARRIVAL_SENTINEL — un test
// d'égalité garde les deux synchrones. Pas d'équivalent « avant 7h » (le
// dessin n'en a pas, verdict Dimitri).
export const LATE_ARRIVAL_SENTINEL = "apres_23h"

// ── Tokens du proto ABSENTS de la charte séjour ────────────────────────────
// Relevés au mot sur formulaire-proto.html. Les couleurs partagées avec la
// charte (or/orBg/orB/orTx, vert*, inkSoft, ciel, sejourFond) viennent de C —
// y compris ses variantes MESURÉES pour l'accessibilité (vert #1D9E75 au lieu
// du #2f9e6e du proto : écart assumé, règle charte « ne pas revenir »).
const P = {
  fond: "#f8fafc", // .vue background
  line: "#e3e8ee", // bordures champs
  red: "#e6c4b8", // bordure champ en erreur
  redTx: "#8f2f1d", // texte d'erreur sous champ
  redBg: "#fdf4f0", // avis.mal
  cielBg: "#e8f2fa", // cibles pièce + bouton pied
  cielB: "#c3d5e4", // bordure bouton pied
  vertB: "#c2ded0", // bordure ligne fichier verte + liseré carte « faite »
  ctaDisabled: "#c3cdd6", // .cta:disabled
}

// ── Rangement des références standard en cartes (pièce 1 du plan) ──────────
const CARD_OF_REF = {
  email: "vous",
  phone: "vous",
  billing_address: "vous",
  time_in: "horaires",
  time_out: "horaires",
  id_type: "piece",
  passport_photo: "piece",
  id_recto: "piece",
  id_verso: "piece",
  ts_recto: "piece",
  ts_verso: "piece",
  notes: "mot",
  comments: "mot",
}
const CARD_ORDER = ["vous", "horaires", "piece", "mot", "autre"]
const CARD_ICONS = { vous: "○", horaires: "◷", piece: "▤", mot: "✎", autre: "⊕" }

// Répartit les fields en cartes. Champ CUSTOM (référence hors carte connue) →
// carte du DERNIER champ standard qui le précède dans l'ordre admin (héritage
// positionnel, décision 7) ; sans ancre → carte « Autre », rendue en dernier.
export function assignFieldsToCards(fields) {
  const byCard = { vous: [], horaires: [], piece: [], mot: [], autre: [] }
  let lastCard = null
  for (const f of fields || []) {
    if (!f) continue
    const std = CARD_OF_REF[f.reference]
    if (std) {
      byCard[std].push(f)
      lastCard = std
    } else {
      byCard[lastCard || "autre"].push(f)
    }
  }
  return byCard
}

// Un champ est-il « rempli » (coche verte / anneau / bouton parlant) ?
function fieldFilled(f, values, uploads) {
  if (f.type === "file") return (uploads[f.reference] || {}).status === "done"
  const v = values[f.reference]
  return !(v == null || v === "" || (Array.isArray(v) && v.length === 0))
}

// État des cartes : comptables (anneau + bouton, jamais « mot »), faites,
// manquantes. La carte pièce compte « faite » dès l'upload confirmé — l'ambre
// n'empêche JAMAIS l'envoi (fail-open gravé).
export function computeCardsState({ fields, values, uploads, idDocRequired, billingAddressRequired, lang }) {
  const byCard = assignFieldsToCards(fields)
  const cards = []
  for (const key of CARD_ORDER) {
    const cardFields = byCard[key].filter((f) =>
      shouldRenderField(f, values, idDocRequired, billingAddressRequired),
    )
    // La carte pièce garde ses champs conditionnels cachés (uploads selon
    // id_type) hors du calcul, mais la carte se rend dès qu'un field pièce
    // est configuré ET que la pièce est requise pour ce canal.
    const anyConfigured = byCard[key].length > 0
    if (!anyConfigured) continue
    if (key === "piece" && idDocRequired === false) continue
    if (cardFields.length === 0 && key !== "piece") continue
    const required = cardFields.filter((f) => f.required)
    const countable = key !== "mot" && required.length > 0
    const done = required.length > 0 && required.every((f) => fieldFilled(f, values, uploads))
    cards.push({ key, fields: cardFields, countable, done })
  }
  const countable = cards.filter((c) => c.countable)
  const faites = countable.filter((c) => c.done).length
  const total = countable.length
  const manque = countable.filter((c) => !c.done).map((c) => cardTitle(lang, c.key).toLowerCase())
  return { cards, faites, total, manque }
}

function cardTitle(lang, key) {
  const map = { vous: "v2_carte_vous", horaires: "v2_carte_horaires", piece: "v2_carte_piece", mot: "v2_carte_mot", autre: "v2_carte_autre" }
  return t(lang, map[key])
}

// ── Formats d'affichage ────────────────────────────────────────────────────
// Valeur canonique « 16:30 » → affichage FR « 16h30 » (EN/ES : « 16:30 »).
export function formatHeure(lang, hhmm) {
  if (hhmm === LATE_ARRIVAL_SENTINEL) return t(lang, "v2_apres_23h")
  const m = String(hhmm || "").match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return hhmm || ""
  if (lang !== "fr") return `${m[1]}:${m[2]}`
  return m[2] === "00" ? `${parseInt(m[1], 10)}h` : `${parseInt(m[1], 10)}h${m[2]}`
}

const MONTH_LOCALE = { fr: "fr-FR", en: "en-GB", es: "es-ES" }
function monthName(lang, d) {
  try {
    return d.toLocaleDateString(MONTH_LOCALE[lang] || "fr-FR", { month: "long" })
  } catch (_) {
    return ""
  }
}

// « du 20 au 24 septembre » / « du 28 septembre au 2 octobre » (maquette).
export function formatPeriode(lang, startIso, endIso) {
  const d1 = new Date(startIso)
  const d2 = new Date(endIso)
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return ""
  const sameMonth = d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear()
  if (lang === "en") {
    return sameMonth
      ? `from ${monthName(lang, d1)} ${d1.getDate()} to ${d2.getDate()}`
      : `from ${monthName(lang, d1)} ${d1.getDate()} to ${monthName(lang, d2)} ${d2.getDate()}`
  }
  if (lang === "es") {
    return sameMonth
      ? `del ${d1.getDate()} al ${d2.getDate()} de ${monthName(lang, d2)}`
      : `del ${d1.getDate()} de ${monthName(lang, d1)} al ${d2.getDate()} de ${monthName(lang, d2)}`
  }
  return sameMonth
    ? `du ${d1.getDate()} au ${d2.getDate()} ${monthName(lang, d2)}`
    : `du ${d1.getDate()} ${monthName(lang, d1)} au ${d2.getDate()} ${monthName(lang, d2)}`
}

// « 16h le 20 septembre » (écran merci).
export function formatHeureLeDate(lang, hhmm, iso) {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return formatHeure(lang, hhmm)
  const jour = `${d.getDate()} ${monthName(lang, d)}`
  if (lang === "en") return `${formatHeure(lang, hhmm)} on ${monthName(lang, d)} ${d.getDate()}`
  return `${formatHeure(lang, hhmm)} le ${jour}`
}

// Pastille temporelle « Arrivée dans X jours ».
export function pastilleArrivee(lang, startIso, now = new Date()) {
  if (!startIso) return null
  const d = new Date(`${String(startIso).slice(0, 10)}T00:00:00`)
  if (isNaN(d.getTime())) return null
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const days = Math.round((d - today) / 86400000)
  if (days < 0) return null
  if (days === 0) return t(lang, "v2_quand_aujourdhui")
  if (days === 1) return t(lang, "v2_quand_demain")
  return t(lang, "v2_quand_jours", { n: days })
}

// Phrase d'anneau (maquette : « Plus que X. » / « Il reste X et Y. » /
// « Tout est rempli, vous pouvez envoyer. »).
export function phraseAnneau(lang, manque) {
  if (!manque || manque.length === 0) return t(lang, "v2_tout_rempli")
  if (manque.length === 1) return t(lang, "v2_plus_que", { x: manque[0] })
  return t(lang, "v2_il_reste", {
    liste: manque.slice(0, -1).join(", "),
    dernier: manque[manque.length - 1],
  })
}

// Composition CLIENT du verdict ambre depuis les ÉNUMS serveur (J2 — aucun
// texte libre du modèle n'atteint jamais le voyageur). null → pas d'ambre.
const DEFAUT_KEYS = {
  flou: "flou", reflet: "reflet", cadrage: "cadrage", sombre: "sombre",
  "tronqué": "tronque", tronque: "tronque",
  nom_illisible: "nom", date_illisible: "date",
  "périmé": "perime", perime: "perime",
}
const ZONE_KEYS = { haut: "v2_zone_haut", bas: "v2_zone_bas", gauche: "v2_zone_gauche", droite: "v2_zone_droite" }
export function composeAmbre(lang, controle) {
  if (!controle) return null
  if (controle.nom_correspond === "mismatch") {
    return { titre: t(lang, "v2_ambre_mismatch_titre"), conseil: t(lang, "v2_ambre_mismatch_conseil") }
  }
  const key = DEFAUT_KEYS[controle.defaut]
  if (!key) return null
  const zone = ZONE_KEYS[controle.zone] ? t(lang, ZONE_KEYS[controle.zone]) : ""
  return {
    titre: t(lang, `v2_ambre_${key}_titre`, { zone }),
    conseil: t(lang, `v2_ambre_${key}_conseil`),
  }
}

// Ligne verte : « Document reçu » quand le contrôle est vert — le DÉTAIL de
// ce que le contrôle a lu (lisibilité, année de validité, nom) ne s'affiche
// JAMAIS côté voyageur (verdict revue arrêt 4 : « Le détail du contrôle,
// c'est mon affaire, pas la sienne ») ; il reste visible côté admin.
// Sans verdict → neutre « Document envoyé » (fail-open). `unknown` = neutre,
// jamais ambre.
export function verdictVertTexte(lang, controle) {
  if (!controle || controle.lisible !== true) return t(lang, "v2_doc_envoye")
  if (composeAmbre(lang, controle)) return t(lang, "v2_doc_envoye")
  if (controle.annee_validite && controle.nom_correspond === "match") {
    return t(lang, "v2_doc_recu")
  }
  return t(lang, "v2_doc_envoye")
}

// ── Créneaux horaires (deux sélecteurs, le dessin fait foi) ────────────────
function toMin(s) {
  const m = String(s || "").match(/^(\d{1,2}):(\d{2})$/)
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null
}
function toHHMM(m) {
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`
}
// Arrivée : de checkin_start à 23:00, pas de 30 min, + sentinelle « après 23h ».
export function buildArrivalSlots(checkinStart) {
  const lo = toMin(checkinStart) ?? toMin("15:00")
  const out = []
  for (let m = Math.ceil(lo / 30) * 30; m <= 23 * 60; m += 30) out.push(toHHMM(m))
  out.push(LATE_ARRIVAL_SENTINEL)
  return out
}
// Départ : de 07:00 à checkout_time, pas de 30 min.
export function buildDepartureSlots(checkoutTime) {
  const hi = toMin(checkoutTime) ?? toMin("11:00")
  const out = []
  for (let m = 7 * 60; m <= hi; m += 30) out.push(toHHMM(m))
  return out
}

// ── Styles partagés ────────────────────────────────────────────────────────
const pageWrap = {
  minHeight: "100vh",
  background: P.fond,
  fontFamily: F.sans,
  color: C.ink,
  display: "flex",
  flexDirection: "column",
}
const colonne = { width: "100%", maxWidth: 430, margin: "0 auto", flex: 1, display: "flex", flexDirection: "column" }
const inputBase = {
  width: "100%",
  minHeight: 50,
  borderRadius: 12,
  border: `1px solid ${P.line}`,
  background: "#fff",
  padding: "0 14px",
  fontSize: 15,
  fontFamily: F.sans,
  color: C.ink,
  outline: "none",
}
// ── Style partagé des <select> (verdict arrêt 1, sélecteur « énorme ») ──────
// DIAGNOSTIC (2026-09-06, repro 1280×720) : police et héritage HORS DE CAUSE —
// 15 px mesurés sur les 3 selects ET leurs options, colonne à 430 px. La seule
// différence avec les selects de la maison qui n'ont jamais montré le défaut
// (formulaire V1, séjour) : V2 laissait `appearance: auto` — le navigateur
// dérive alors le rendu du menu ouvert des métriques du contrôle stylé
// (50 px de haut, pleine colonne, arrondi), d'où un menu démesuré sur les
// rendus Chrome récents. CAUSE traitée : on reprend la recette maison —
// `appearance: none` + chevron dessiné (GuestFormView V1 l.526/678) — le menu
// suit la police (15 px), plus les métriques du contrôle. Appliqué aux TROIS
// sélecteurs (Quel document ? + les deux horaires, même type).
const CHEVRON_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%232e3d4d' stroke-width='1.8' stroke-linecap='round'/%3E%3C/svg%3E")`
const selectBase = {
  ...inputBase,
  cursor: "pointer",
  appearance: "none",
  WebkitAppearance: "none",
  backgroundImage: CHEVRON_SVG,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 16px center",
  paddingRight: 40,
}

const labelStyle = { display: "block", fontSize: 12.5, fontWeight: 600, marginBottom: 6 }
const aideStyle = { display: "block", fontSize: 11.5, color: C.inkSoft, marginBottom: 7, lineHeight: 1.45 }

function V2GlobalStyles() {
  return (
    <style>{`
      .fbz2-input:focus { border-color: ${C.ciel} !important; box-shadow: 0 0 0 3px rgba(135,184,230,.18); }
      @media (max-width: 540px) { .fbz2-input { font-size: 16px !important; } }
    `}</style>
  )
}

// Ligne de marque du formulaire (composée SUR PLACE — elle diffère de la
// LigneMarque du séjour, lotus seul par décision Dimitri) : lotus à gauche,
// sélecteur FR/EN/ES à droite (maquette .heroL).
function LigneMarqueForm({ lang, onChangeLang }) {
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 0 }}>
      <img
        src={`data:image/png;base64,${LOGO_B64}`}
        alt=""
        aria-hidden="true"
        // 36 px — aligné sur la LigneMarque du séjour (révision Dimitri
        // 2026-09-05 : « trop discret » en dessous).
        style={{ width: 36, height: 36, display: "block" }}
      />
      <span
        style={{
          marginLeft: "auto",
          display: "flex",
          gap: 2,
          background: "rgba(255,255,255,.12)",
          borderRadius: 9,
          padding: 3,
        }}
      >
        {FORM_LOCALES.map((loc) => (
          <button
            key={loc}
            type="button"
            onClick={() => onChangeLang(loc)}
            aria-pressed={lang === loc}
            style={{
              minWidth: 46,
              minHeight: 44,
              borderRadius: 8,
              fontSize: 12,
              fontFamily: F.sans,
              fontWeight: lang === loc ? 600 : 500,
              border: "none",
              cursor: "pointer",
              background: lang === loc ? "rgba(255,255,255,.9)" : "transparent",
              color: lang === loc ? C.sejourFond : "rgba(255,255,255,.7)",
            }}
          >
            {loc.toUpperCase()}
          </button>
        ))}
      </span>
    </span>
  )
}

// ── Cartes-sections ────────────────────────────────────────────────────────
function CarteSection({ icone, titre, sous, done, children, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        background: "#fff",
        borderRadius: 16,
        marginBottom: 11,
        overflow: "hidden",
        boxShadow: done
          ? `0 1px 3px rgba(35,50,64,.05), 0 0 0 1px ${P.vertB}`
          : "0 2px 10px rgba(35,50,64,.06), 0 0 0 1px rgba(35,50,64,.05)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "15px 15px 0" }}>
        <span
          aria-hidden="true"
          style={{
            width: 36,
            height: 36,
            borderRadius: 11,
            background: done ? C.vertBg : C.ciel,
            color: done ? C.vertTx : "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontSize: 14,
          }}
        >
          {icone}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <b style={{ display: "block", fontSize: 15, fontWeight: 600, lineHeight: 1.3, letterSpacing: "-0.01em" }}>
            {titre}
          </b>
          {sous ? (
            <span style={{ display: "block", fontSize: 11.5, color: C.inkSoft, marginTop: 2, lineHeight: 1.4 }}>
              {sous}
            </span>
          ) : null}
        </span>
        {done ? (
          <span
            data-testid={`coche-${testId}`}
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: C.vert,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              flexShrink: 0,
            }}
          >
            ✓
          </span>
        ) : null}
      </div>
      <div style={{ padding: "13px 15px 15px" }}>{children}</div>
    </div>
  )
}

// Erreur sous champ (maquette .ch .err).
function ErreurChamp({ children }) {
  return (
    <span
      role="alert"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 7,
        marginTop: 6,
        fontSize: 11.5,
        color: P.redTx,
        lineHeight: 1.45,
      }}
    >
      ⚠ {children}
    </span>
  )
}

// ── Champ générique V2 ─────────────────────────────────────────────────────
// Libellés maquette pour les champs standard NON personnalisés (le libellé
// admin prime dès qu'il diffère du défaut produit).
const V2_LABEL_OVERRIDES = {
  email: { def: "Email", key: "v2_label_email" },
  phone: { def: "Téléphone", key: "v2_label_phone" },
  notes: { def: "Notes", key: "v2_label_notes" },
  id_type: { def: "Type de pièce d'identité", key: "v2_quel_document" },
  passport_photo: { def: "Page d'identité du passeport", key: "v2_page_photo" },
}
function v2Label(lang, field) {
  const o = V2_LABEL_OVERRIDES[field.reference]
  if (o && String(field.label || "").trim() === o.def) return t(lang, o.key)
  return field.label
}

function ChampV2({ field, value, onChange, lang, error, optionnel }) {
  const err = !!error
  const style = { ...inputBase, ...(err ? { borderColor: P.red } : null) }
  const options =
    typeof field.options === "string"
      ? field.options.split("\n").map((s) => s.trim()).filter(Boolean)
      : Array.isArray(field.options)
        ? field.options
        : []
  let control
  if (field.type === "textarea") {
    control = (
      <textarea
        className="fbz2-input"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.reference === "notes" && !field.placeholder ? t(lang, "v2_ph_notes") : field.placeholder || ""}
        style={{ ...style, padding: "13px 14px", minHeight: 88, resize: "vertical", lineHeight: 1.5 }}
      />
    )
  } else if (field.type === "dropdown" || field.type === "radio") {
    // V2 : le choix (dont id_type) se rend en SELECT (maquette « Quel
    // document ? ») — la VALEUR reste canonique FR (piège id_type CLAUDE.md).
    control = (
      <select
        className="fbz2-input"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...selectBase, ...(err ? { borderColor: P.red } : null) }}
      >
        <option value="">—</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {field.reference === "id_type" ? pieceOptionLabel(lang, opt) : opt}
          </option>
        ))}
      </select>
    )
  } else if (field.type === "checkbox") {
    const arr = Array.isArray(value) ? value : []
    control = (
      <div>
        {options.map((opt) => (
          <label key={opt} style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 44, fontSize: 14 }}>
            <input
              type="checkbox"
              checked={arr.includes(opt)}
              onChange={() => onChange(arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt])}
              style={{ width: 18, height: 18, accentColor: C.sejourFond }}
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    )
  } else if (field.type === "date") {
    control = <input className="fbz2-input" type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} style={style} />
  } else {
    control = (
      <input
        className="fbz2-input"
        type={field.input_type || "text"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder || ""}
        readOnly={field.lectureSeule === true || undefined}
        style={style}
      />
    )
  }
  return (
    <div data-fieldref={field.reference} style={{ marginBottom: 13 }}>
      <label style={labelStyle}>
        {v2Label(lang, field)}
        {optionnel ? (
          <span style={{ fontWeight: 400, color: "#98a2ad", fontSize: 11.5 }}> {t(lang, "v2_facultatif")}</span>
        ) : null}
      </label>
      {control}
      {/* `aide` : posée par champsEnModification (courriel déjà reçu). */}
      {field.aide ? (
        <div data-testid={`aide-${field.reference}`} style={{ fontSize: 11.5, color: "#6b7785", marginTop: 6 }}>{field.aide}</div>
      ) : null}
      {error ? <ErreurChamp>{error}</ErreurChamp> : null}
    </div>
  )
}

// ── Upload de pièce V2 ─────────────────────────────────────────────────────
// Cibles « Prendre en photo » / « Choisir un fichier », ligne verte, encart
// ambre, ✕ réel. JAMAIS le nom du fichier (B1 : c'est ce qui rend la
// restauration après rechargement possible).
function CibleUpload({ icone, titre, sous, onPick, capture }) {
  const inputRef = useRef(null)
  return (
    <button
      type="button"
      onClick={() => inputRef.current && inputRef.current.click()}
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
        minHeight: 96,
        borderRadius: 14,
        background: P.cielBg,
        border: `1px dashed ${C.ciel}`,
        color: C.bleu,
        cursor: "pointer",
        fontFamily: F.sans,
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 20 }}>{icone}</span>
      <b style={{ fontSize: 12.5, fontWeight: 600 }}>{titre}</b>
      <span style={{ fontSize: 10.5, color: C.inkSoft }}>{sous}</span>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        {...(capture ? { capture: "environment" } : null)}
        onChange={(e) => onPick(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
        style={{ display: "none" }}
      />
    </button>
  )
}

function UploadPieceV2({ field, uploadState, onPick, onRemove, lang, docLabel, guestDisplayName, error }) {
  const status = uploadState && uploadState.status
  const controle = uploadState && uploadState.controle
  const ambre = status === "done" ? composeAmbre(lang, controle) : null

  const cibles = (reprendre) => (
    <div style={{ display: "flex", gap: 9 }}>
      <CibleUpload
        icone="▣"
        titre={reprendre ? t(lang, "v2_reprendre_photo") : t(lang, "v2_prendre_photo")}
        sous={t(lang, "v2_appareil")}
        capture
        onPick={onPick}
      />
      <CibleUpload icone="⊕" titre={t(lang, "v2_choisir_fichier")} sous={t(lang, "v2_depuis_tel")} onPick={onPick} />
    </div>
  )

  let contenu
  if (status === "uploading" || status === "checking") {
    contenu = (
      <div
        role="status"
        style={{
          display: "flex", alignItems: "center", gap: 11, background: "#fff",
          border: `1px solid ${P.line}`, borderRadius: 12, padding: "11px 12px",
          fontSize: 12, color: C.inkSoft, minHeight: 44,
        }}
      >
        <span aria-hidden="true">⏳</span>
        {t(lang, status === "checking" ? "v2_verif_doc" : "v2_envoi_doc")}
      </div>
    )
  } else if (status === "done" && ambre) {
    contenu = (
      <>
        <div
          data-testid={`ambre-${field.reference}`}
          style={{
            display: "flex", gap: 11, background: C.orBg, border: `1px solid ${C.orB}`,
            borderRadius: 12, padding: 12, marginBottom: 9,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 32, height: 32, borderRadius: 9, background: "#fff",
              border: `1px solid ${C.orB}`, color: C.orTx, display: "flex",
              alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 13,
            }}
          >
            !
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <b style={{ display: "block", fontSize: 12, color: C.orTx }}>{ambre.titre}</b>
            <span style={{ display: "block", fontSize: 11.5, color: C.orTx, marginTop: 3, lineHeight: 1.5 }}>
              {ambre.conseil}
            </span>
          </span>
        </div>
        {cibles(true)}
      </>
    )
  } else if (status === "done") {
    contenu = (
      <div
        data-testid={`fichier-${field.reference}`}
        style={{
          display: "flex", alignItems: "center", gap: 11, background: C.vertBg,
          border: `1px solid ${P.vertB}`, borderRadius: 12, padding: "11px 12px",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 32, height: 32, borderRadius: 9, background: "#fff",
            border: `1px solid ${P.vertB}`, color: C.vertTx,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 13,
          }}
        >
          ✓
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <b style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.vertTx }}>
            {docLabel}
            {guestDisplayName ? ` · ${guestDisplayName}` : ""}
          </b>
          <span style={{ display: "block", fontSize: 10.5, color: C.vertTx, opacity: 0.75, marginTop: 1 }}>
            {verdictVertTexte(lang, controle)}
          </span>
        </span>
        {onRemove ? (
          <button
            type="button"
            aria-label={t(lang, "v2_retirer_doc")}
            onClick={onRemove}
            style={{
              minWidth: 44, minHeight: 44, borderRadius: 10, color: C.vertTx,
              fontSize: 13, flexShrink: 0, border: "none", background: "none", cursor: "pointer",
            }}
          >
            ✕
          </button>
        ) : null}
      </div>
    )
  } else {
    contenu = (
      <>
        {status === "failed" ? (
          <ErreurChamp>{(uploadState && uploadState.error) || t(lang, "upload_err_generic")}</ErreurChamp>
        ) : null}
        {cibles(status === "failed")}
      </>
    )
  }

  return (
    <div data-fieldref={field.reference} style={{ marginBottom: 13 }}>
      <label style={labelStyle}>{v2Label(lang, field)}</label>
      {field.reference === "passport_photo" || String(field.label || "") === "La page avec votre photo" ? (
        <span style={aideStyle}>{t(lang, "v2_aide_photo")}</span>
      ) : null}
      {contenu}
      {error ? <ErreurChamp>{error}</ErreurChamp> : null}
    </div>
  )
}

// ── Horaires : deux sélecteurs côte à côte ─────────────────────────────────
function SelectHoraire({ label, slots, value, onChange, lang, error }) {
  return (
    <div data-fieldref-horaire="" style={{ flex: 1, minWidth: 0 }}>
      <label style={labelStyle}>{label}</label>
      <select
        className="fbz2-input"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...selectBase, ...(error ? { borderColor: P.red } : null) }}
      >
        <option value="">—</option>
        {slots.map((s) => (
          <option key={s} value={s}>
            {formatHeure(lang, s)}
          </option>
        ))}
      </select>
    </div>
  )
}

function DuoHoraires({ timeIn, timeOut, values, onSetValue, schedule, lang, fieldErrors }) {
  const errIn = timeIn && fieldErrors ? fieldErrors[timeIn.reference] : null
  const errOut = timeOut && fieldErrors ? fieldErrors[timeOut.reference] : null
  return (
    <div data-fieldref={timeIn ? timeIn.reference : "time_in"}>
      <div style={{ display: "flex", gap: 9 }}>
        {timeIn ? (
          <SelectHoraire
            label={t(lang, "v2_label_arrivee")}
            slots={buildArrivalSlots(schedule && schedule.checkin_start)}
            value={values[timeIn.reference]}
            onChange={(v) => onSetValue(timeIn.reference, v)}
            lang={lang}
            error={errIn}
          />
        ) : null}
        {timeOut ? (
          <SelectHoraire
            label={t(lang, "v2_label_depart")}
            slots={buildDepartureSlots(schedule && schedule.checkout_time)}
            value={values[timeOut.reference]}
            onChange={(v) => onSetValue(timeOut.reference, v)}
            lang={lang}
            error={errOut}
          />
        ) : null}
      </div>
      {errIn ? <ErreurChamp>{errIn}</ErreurChamp> : null}
      {errOut && !errIn ? <ErreurChamp>{errOut}</ErreurChamp> : null}
      <span style={{ display: "block", fontSize: 11.5, color: C.inkSoft, lineHeight: 1.5, marginTop: 4 }}>
        {t(lang, "v2_horaires_phrase", {
          checkin: formatHeure(lang, (schedule && schedule.checkin_start) || "15:00"),
          checkout: formatHeure(lang, (schedule && schedule.checkout_time) || "11:00"),
        })}
        <a href={`mailto:${FORM_CONTACT_EMAIL}`} style={{ color: C.bleu, fontWeight: 500 }}>
          {t(lang, "v2_hc_lien")}
        </a>
        {t(lang, "v2_hc_suite")}
      </span>
    </div>
  )
}

// ── Bloc RGPD repliable (V2) ───────────────────────────────────────────────
// Titre « Ce que nous faisons de votre pièce » ; corps = paragraphe CONTRÔLE
// IA validé au mot par Dimitri PUIS le rgpd_body existant inchangé (15 jours,
// contact@). Le corps maquette (« jamais transmis à un tiers ») est ÉCARTÉ —
// devenu faux avec le contrôle IA (décision GO 2026-09-06).
export function BlocRgpdV2({ lang, open, onToggle }) {
  const body = t(lang, "rgpd_body")
  const [before, after] = body.split(FORM_CONTACT_EMAIL)
  return (
    <div
      style={{
        background: "#fff", borderRadius: 15, marginBottom: 11, overflow: "hidden",
        boxShadow: "0 1px 3px rgba(35,50,64,.07), 0 0 0 1px rgba(35,50,64,.05)",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          display: "flex", alignItems: "center", gap: 11, width: "100%", textAlign: "left",
          padding: "13px 14px", minHeight: 54, border: "none", background: "none",
          cursor: "pointer", fontFamily: F.sans, color: C.ink,
        }}
      >
        <b style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600 }}>{t(lang, "v2_rgpd_titre")}</b>
        <span aria-hidden="true" style={{ fontSize: 16, color: "#98a2ad", transform: open ? "rotate(90deg)" : "none" }}>
          ›
        </span>
      </button>
      {open ? (
        <div style={{ padding: "0 14px 14px" }}>
          <p style={{ fontSize: 11.5, color: C.inkSoft, lineHeight: 1.6, margin: 0 }}>
            {t(lang, "v2_rgpd_controle")}
          </p>
          <p style={{ fontSize: 11.5, color: C.inkSoft, lineHeight: 1.6, margin: "9px 0 0" }}>
            {before}
            <a href={`mailto:${FORM_CONTACT_EMAIL}`} style={{ color: C.bleu, fontWeight: 600 }}>
              {FORM_CONTACT_EMAIL}
            </a>
            {after}
          </p>
        </div>
      ) : null}
    </div>
  )
}

// ── Pied ───────────────────────────────────────────────────────────────────
function PiedV2({ lang }) {
  return (
    <div style={{ padding: "20px 15px 24px", textAlign: "center", borderTop: `1px solid ${P.line}`, marginTop: 18 }}>
      <a
        href={`mailto:${FORM_CONTACT_EMAIL}`}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 9,
          minHeight: 48, padding: "0 20px", borderRadius: 14, background: P.cielBg,
          border: `1px solid ${P.cielB}`, fontSize: 13, color: C.sejourFond,
          fontWeight: 600, textDecoration: "none", fontFamily: F.sans,
        }}
      >
        {t(lang, "v2_pied_question")}
      </a>
    </div>
  )
}

// ── Barre d'action sticky ──────────────────────────────────────────────────
function BarreCta({ children, sous, disabled, testId }) {
  return (
    <div
      style={{
        position: "sticky", bottom: 0, zIndex: 5,
        background: "linear-gradient(to top, #fff 58%, rgba(255,255,255,0))",
        padding: "14px 15px 15px", flexShrink: 0,
      }}
    >
      <button
        type="submit"
        data-testid={testId || "formv2-cta"}
        disabled={disabled}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 9,
          width: "100%", minHeight: 52, borderRadius: 14,
          background: disabled ? P.ctaDisabled : C.sejourFond,
          color: "#fff", fontSize: 15, fontWeight: 600, fontFamily: F.sans, border: "none",
          boxShadow: disabled ? "none" : "0 3px 14px rgba(35,50,64,.22)",
          cursor: disabled ? "default" : "pointer",
        }}
      >
        {children}
      </button>
      {sous ? (
        <span
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center",
            fontSize: 11.5, color: C.inkSoft, marginTop: 9, minHeight: 44, lineHeight: 1.4, padding: "0 8px",
          }}
        >
          {sous}
        </span>
      ) : null}
    </div>
  )
}

// ═══ Composant principal ═══════════════════════════════════════════════════
export default function GuestFormViewV2({
  formConfig, logementName, reservation, schedule, idDocRequired, billingAddressRequired,
  values, uploads, lang, step, error,
  onSetValue, onChangeLang, onSubmit, onPickIdFile,
  guestFirstName = null, guestDisplayName = null, journeyUrl = null,
  fieldErrors = null, onRemoveIdFile = null,
  rgpdOpen = false, onToggleRgpd = null,
  previewMode = false,
  // { jusqu_au, avec_heure } | null — limite de modification servie par
  // /api/form-config (limite_modification). null → aucune promesse affichée.
  limiteModification = null,
}) {
  const fields = (formConfig && formConfig.fields) || []
  const etat = computeCardsState({ fields, values, uploads, idDocRequired, billingAddressRequired, lang })
  const rootRef = useRef(null)

  // Défilement au premier champ fautif quand la carte d'erreurs change (B2).
  const errKey = fieldErrors ? Object.keys(fieldErrors).join("|") : ""
  useEffect(() => {
    if (!errKey || !rootRef.current) return
    const first = errKey.split("|")[0]
    const el = rootRef.current.querySelector(`[data-fieldref="${first}"]`)
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }, [errKey])

  const titre = guestFirstName
    ? t(lang, "v2_bonjour", { prenom: guestFirstName })
    : t(lang, "v2_bonjour_sans_prenom")
  const quand = reservation ? pastilleArrivee(lang, reservation.start_date) : null
  const dates =
    reservation && reservation.start_date && reservation.end_date
      ? `${logementName} · ${formatPeriode(lang, reservation.start_date, reservation.end_date)}`
      : logementName

  const submitting = step === "submitting"
  const pret = etat.manque.length === 0
  const docTypeLabel = values.id_type ? pieceOptionLabel(lang, values.id_type) : null

  const renderCard = (card) => {
    const key = card.key
    const err = (ref) => (fieldErrors ? fieldErrors[ref] : null)
    let inner = null
    if (key === "horaires") {
      const timeIn = card.fields.find((f) => f.reference === "time_in")
      const timeOut = card.fields.find((f) => f.reference === "time_out")
      const autres = card.fields.filter((f) => f !== timeIn && f !== timeOut)
      inner = (
        <>
          <DuoHoraires
            timeIn={timeIn}
            timeOut={timeOut}
            values={values}
            onSetValue={onSetValue}
            schedule={schedule}
            lang={lang}
            fieldErrors={fieldErrors}
          />
          {autres.map((f) => (
            <ChampV2 key={f.id || f.reference} field={f} value={values[f.reference]} onChange={(v) => onSetValue(f.reference, v)} lang={lang} error={err(f.reference)} optionnel={!f.required} />
          ))}
        </>
      )
    } else {
      inner = card.fields.map((f) => {
        if (f.type === "file") {
          return (
            <UploadPieceV2
              key={f.id || f.reference}
              field={f}
              uploadState={uploads[f.reference]}
              onPick={(file) => onPickIdFile(f.reference, file)}
              onRemove={onRemoveIdFile ? () => onRemoveIdFile(f.reference) : null}
              lang={lang}
              docLabel={docTypeLabel || v2Label(lang, f)}
              guestDisplayName={guestDisplayName}
              error={err(f.reference)}
            />
          )
        }
        return (
          <ChampV2
            key={f.id || f.reference}
            field={f}
            value={values[f.reference]}
            onChange={(v) => onSetValue(f.reference, v)}
            lang={lang}
            error={err(f.reference)}
            // Verdict Dimitri (arrêt 1) : sur la carte « Un mot pour nous »,
            // « facultatif » n'est dit qu'UNE fois — par le sous-titre de la
            // carte, jamais en plus à côté du libellé du champ.
            optionnel={key === "mot" ? false : !f.required}
          />
        )
      })
    }
    return (
      <CarteSection
        key={key}
        testId={`carte-${key}`}
        icone={CARD_ICONS[key]}
        titre={cardTitle(lang, key)}
        sous={key === "piece" ? t(lang, "v2_carte_piece_sous") : key === "mot" ? t(lang, "v2_facultatif") : null}
        done={card.countable ? card.done : card.fields.some((f) => fieldFilled(f, values, uploads))}
      >
        {inner}
      </CarteSection>
    )
  }

  return (
    <div ref={rootRef} style={pageWrap}>
      <V2GlobalStyles />
      <div style={colonne}>
        {previewMode ? (
          <div
            style={{
              padding: "8px 12px", fontSize: 11.5, fontWeight: 700, color: C.bleu,
              background: P.cielBg, border: `1px dashed ${C.ciel}`, borderRadius: 10, margin: "8px 8px 0",
            }}
          >
            Aperçu voyageur (même rendu que le vrai formulaire) — soumission désactivée.
          </div>
        ) : null}
        <Bandeau
          testId="formv2-bandeau"
          titre={titre}
          quand={quand}
          dates={dates}
          anneau={etat.total ? { faites: etat.faites, total: etat.total } : null}
          ligne={
            etat.total
              ? [
                  t(lang, etat.faites > 1 ? "v2_faits" : "v2_fait", { n: etat.faites, tot: etat.total }),
                  phraseAnneau(lang, etat.manque),
                ]
              : null
          }
          marque={<LigneMarqueForm lang={lang} onChangeLang={onChangeLang} />}
          photo={resolveCoverPhoto(formConfig)}
        />
        <form onSubmit={onSubmit} noValidate style={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <div style={{ padding: "1px 15px 0", flex: 1 }}>
            {/* Q-TITRE-DESC — le texte d'intro admin SURVIT en court paragraphe
                sous le bandeau ; le titre configuré ne se rend plus (« Bonjour
                {prénom} » le remplace). */}
            {formConfig && formConfig.description ? (
              <p
                style={{
                  fontSize: 12.5, color: C.inkSoft, lineHeight: 1.55,
                  margin: "2px 2px 13px", whiteSpace: "pre-line",
                }}
              >
                {formConfig.description}
              </p>
            ) : null}
            {error ? (
              <div
                role="alert"
                style={{
                  borderRadius: 14, padding: "12px 13px", marginBottom: 11, fontSize: 12.5,
                  lineHeight: 1.6, background: P.redBg, border: `1px solid ${P.red}`, color: P.redTx,
                }}
              >
                {error}
              </div>
            ) : null}
            {etat.cards.map(renderCard)}
            {idDocRequired !== false && etat.cards.some((c) => c.key === "piece") ? (
              <BlocRgpdV2 lang={lang} open={rgpdOpen} onToggle={onToggleRgpd || (() => {})} />
            ) : null}
            <PiedV2 lang={lang} />
          </div>
          <BarreCta
            disabled={submitting || (!pret && !previewMode)}
            sous={
              submitting
                ? t(lang, "v2_ne_fermez_pas")
                : pret
                  ? texteLimiteModification(lang, limiteModification, { date: "v2_sub_pret", veille: "v2_sub_pret_veille" })
                  : t(lang, "v2_sub_persistance")
            }
          >
            {submitting
              ? t(lang, "v2_envoi_en_cours")
              : pret
                ? t(lang, "v2_envoyer")
                : t(lang, "v2_envoyer_manque", { x: etat.manque[0] })}
          </BarreCta>
        </form>
      </div>
    </div>
  )
}

// ═══ Écran MERCI (maquette état « envoyé ») ════════════════════════════════
export function GuestFormMerciV2({ lang, guestFirstName = null, journeyUrl = null, heure = null, dateIso = null, onChangeLang, limiteModification = null }) {
  // Vraie limite (relectures du 28/10) ; rien si elle est passée ou inconnue.
  const texteModif = texteLimiteModification(lang, limiteModification, { date: "v2_merci_modif", veille: "v2_merci_modif_veille" })
  const titre = guestFirstName
    ? t(lang, "v2_merci_titre", { prenom: guestFirstName })
    : t(lang, "v2_merci_titre_sans_prenom")
  return (
    <div style={pageWrap}>
      <div style={colonne}>
        <Bandeau
          testId="formv2-merci-bandeau"
          titre={titre}
          marque={onChangeLang ? <LigneMarqueForm lang={lang} onChangeLang={onChangeLang} /> : null}
        />
        <div style={{ padding: "1px 15px 0", flex: 1 }}>
          <div
            style={{
              background: "#fff", borderRadius: 18, padding: "19px 17px", marginBottom: 11,
              textAlign: "center", boxShadow: "0 2px 12px rgba(35,50,64,.07), 0 0 0 1px rgba(35,50,64,.05)",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: "inline-flex", width: 52, height: 52, borderRadius: 16,
                background: C.vertBg, color: C.vertTx, alignItems: "center",
                justifyContent: "center", fontSize: 22,
              }}
            >
              ✓
            </span>
            <b style={{ display: "block", fontSize: 17, fontWeight: 600, marginTop: 13, lineHeight: 1.35 }}>
              {t(lang, "v2_merci_tout_recu")}
            </b>
            {heure && dateIso ? (
              <p style={{ fontSize: 13, color: C.inkSoft, marginTop: 7, lineHeight: 1.6 }}>
                {t(lang, "v2_merci_arrivee", { h: formatHeure(lang, heure), date: formatDateSeule(lang, dateIso) })}
              </p>
            ) : null}
          </div>
          {texteModif ? (
            <div
              data-testid="formv2-merci-modif"
              style={{
                borderRadius: 14, padding: "12px 13px", marginBottom: 11, fontSize: 12.5,
                lineHeight: 1.6, background: C.vertBg, border: `1px solid ${P.vertB}`, color: C.vertTx,
              }}
            >
              {texteModif}
            </div>
          ) : null}
          <PiedV2 lang={lang} />
        </div>
        {journeyUrl ? (
          <div
            style={{
              position: "sticky", bottom: 0, zIndex: 5,
              background: "linear-gradient(to top, #fff 58%, rgba(255,255,255,0))",
              padding: "14px 15px 15px",
            }}
          >
            <a
              data-testid="formv2-retour-sejour"
              href={journeyUrl}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 9,
                width: "100%", minHeight: 52, borderRadius: 14, background: C.sejourFond,
                color: "#fff", fontSize: 15, fontWeight: 600, fontFamily: F.sans,
                textDecoration: "none", boxShadow: "0 3px 14px rgba(35,50,64,.22)",
              }}
            >
              {t(lang, "v2_retour_sejour")}
            </a>
          </div>
        ) : null}
      </div>
    </div>
  )
}

// « le 20 septembre » sans l'heure (merci).
function formatDateSeule(lang, iso) {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ""
  if (lang === "en") return `${monthName(lang, d)} ${d.getDate()}`
  return `${d.getDate()} ${monthName(lang, d)}`
}

// ═══ Écran EXPIRÉ (dédié, maquette état « lien expiré ») ═══════════════════
export function GuestFormExpireV2({ lang, onChangeLang = null }) {
  return (
    <div style={pageWrap}>
      <div style={colonne}>
        <Bandeau
          testId="formv2-expire-bandeau"
          titre={t(lang, "v2_lien_expire")}
          marque={onChangeLang ? <LigneMarqueForm lang={lang} onChangeLang={onChangeLang} /> : null}
        />
        <div style={{ padding: "1px 15px 0", flex: 1 }}>
          <div
            style={{
              borderRadius: 14, padding: "12px 13px", marginBottom: 14, fontSize: 12.5,
              lineHeight: 1.6, background: P.redBg, border: `1px solid ${P.red}`, color: P.redTx,
            }}
          >
            <b style={{ fontWeight: 600 }}>{t(lang, "v2_expire_titre")}</b>{" "}
            <a href={`mailto:${FORM_CONTACT_EMAIL}`} style={{ color: P.redTx, fontWeight: 600 }}>
              {t(lang, "v2_expire_lien")}
            </a>
            {t(lang, "v2_expire_suite")}
          </div>
          <PiedV2 lang={lang} />
        </div>
      </div>
    </div>
  )
}
